import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { PaymentPublicClient } from '../backend-client/payment-public-backend';
import getEnv from '../utils/getEnv';

const { REACT_APP_AUTH_SERVICE_URL } = getEnv();

export interface User {
  auth0Identity?: string;
  email?: string;
  name?: {
    familyName: string;
    givenName: string;
  };
  anonymous: boolean;
  sessionID: string;
  email_verified?: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

export interface AuthContext {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  login: () => Promise<void>;
  error: unknown;
  // Raw id from a /payment or /complete link — historically an Invoice id,
  // now an ApplicationGroup id (group-scoped payment URLs). Unresolved; use
  // applicationGroupId for anything auth-related.
  invoiceIdOrGroupId: string;
  // The canonical ApplicationGroup id, resolved from the /terms, /payment or
  // /complete link id.
  applicationGroupId: string;
}

const AuthContext = createContext<AuthContext>({
  isLoading: true
} as AuthContext);

export const getId = (term: string): string | undefined => {
  const url = window.location.href;

  const regex = new RegExp(`/${term}/([a-zA-Z0-9]+)`);

  return url.match(regex)?.[1];
};

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [isLoading, setisLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<unknown>();
  const [defaultTncId, setTncId] = useState(
    sessionStorage.getItem('tncId') || ''
  );
  const [defaultInvoiceIdOrGroupId, setInvoiceIdOrGroupId] = useState(
    sessionStorage.getItem('invoiceIdOrGroupId') || ''
  );

  // Raw id from a T&C (/terms) link — may be a Quote id (legacy links) or an
  // ApplicationGroup id (current links). It is resolved to the canonical
  // ApplicationGroup id below before any auth call.
  const tncId =
    new URLSearchParams(location?.search).get('quote') || getId('terms');
  // Raw id from a /payment or /complete link — an Invoice id on legacy
  // links, an ApplicationGroup id on current (group-scoped) links. Resolved
  // to the canonical ApplicationGroup id below before any auth call.
  const invoiceIdOrGroupId = getId('payment') || getId('complete');

  // Canonical ApplicationGroup id resolved from the raw link id. Empty until
  // the resolve completes (or when there is no raw id to resolve).
  const [applicationGroupId, setApplicationGroupId] = useState('');
  const [isIdResolved, setIsIdResolved] = useState(false);

  useEffect(() => {
    if (tncId) {
      setTncId(tncId);
      // Persisted so a later navigation without the id in the URL still has it.
      sessionStorage.setItem('tncId', tncId);
    }
    if (invoiceIdOrGroupId) {
      setInvoiceIdOrGroupId(invoiceIdOrGroupId);
      sessionStorage.setItem('invoiceIdOrGroupId', invoiceIdOrGroupId);
    }
  }, [tncId, invoiceIdOrGroupId]);

  // Resolve the raw link id (a Quote id from /terms, or an Invoice /
  // ApplicationGroup id from /payment//complete) to the canonical
  // ApplicationGroup id before authenticating — so /me always receives a real
  // group id (mirrors foxcom-forms' getApplicationGroup step). No raw id
  // means there is nothing to resolve.
  useEffect(() => {
    const rawId =
      tncId || defaultTncId || invoiceIdOrGroupId || defaultInvoiceIdOrGroupId;
    if (!rawId) {
      setIsIdResolved(true);
      return;
    }
    let cancelled = false;
    new PaymentPublicClient()
      .getApplicationGroupId(rawId)
      .then((groupId) => {
        if (!cancelled) {
          setApplicationGroupId(groupId);
          setIsIdResolved(true);
        }
      })
      .catch(() => {
        // Unresolvable (e.g. a legacy quote not in a group): proceed without a
        // group id — /me returns anonymous and the app falls back to Auth0.
        if (!cancelled) {
          setIsIdResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tncId, defaultTncId, invoiceIdOrGroupId, defaultInvoiceIdOrGroupId]);

  useEffect(() => {
    // Wait until the raw link id is resolved to a group id before calling /me.
    if (!isIdResolved) {
      return;
    }
    let isMounted = true; // Flag to track component mount status

    const getAuthInfo = async () => {
      setisLoading(true);
      try {
        const res = await fetch(`${REACT_APP_AUTH_SERVICE_URL}/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            // Send only the resolved ApplicationGroup id (matches foxcom-forms).
            // Never forward the raw link id: on group-scoped links it is an
            // ApplicationGroup id, and sending it as `invoiceId` makes the
            // auth service look up a non-existent Invoice.
            applicationGroupId
          }
        });
        const result = await res.json();
        if (isMounted) {
          if (result && Object.keys(result).length !== 0) {
            result.email_verified =
              !result?.anonymous && (result?.email?.length ?? 0) > 0;
            setUser(result);
          }
          setisLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setError(error);
          setisLoading(false);
        }
      }
    };
    getAuthInfo();

    return () => {
      isMounted = false; // Cleanup function sets the flag to false on unmount
    };
  }, [isIdResolved, applicationGroupId]);

  const logout = async () => {
    window.location.href = `${REACT_APP_AUTH_SERVICE_URL}/logout`;
  };

  const login = async () => {
    window.location.href = `${REACT_APP_AUTH_SERVICE_URL}/auth0/login?redirectTo=${window.location.origin}`;
  };
  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user && !user.anonymous,
      isLoading,
      logout,
      login,
      error,
      applicationGroupId,
      invoiceIdOrGroupId: invoiceIdOrGroupId || defaultInvoiceIdOrGroupId
    }),
    [
      user,
      isLoading,
      error,
      applicationGroupId,
      invoiceIdOrGroupId,
      defaultInvoiceIdOrGroupId
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContext => {
  return useContext(AuthContext);
};
